import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { bugReportHref, reportablePage } from '@/utils/bugReportLink';
import { isJoLunoHost } from '@/utils/siteHosts';
import { cx } from '@/utils/cx';
import ModuleIcon from '@/components/ModuleIcon';
import styles from './SiteFooter.module.css';

export interface SiteFooterProps {
  className?: string;
  /** Optional class on the inner grid (e.g. Home library micro-type). */
  innerClassName?: string;
  /** Rendered inside the credit span after “© {year} fmdwhsl” (e.g. landing site link). */
  copyrightExtra?: ReactNode;
  /**
   * When true, “©” links to /admin with invisible styling (Home / maintenance parity).
   */
  adminCopyrightHoneypot?: boolean;
  /** Merged with the footer's link buttons (e.g. Jo Luno landing typography). */
  settingsClassName?: string;
  /**
   * Adds "Make a suggestion" and "Report a bug" (with this page filled in) in
   * the middle of the footer. Every Doopey's World page passes it; it needs
   * the router. Not shown on the Jo Luno host, which has neither page.
   */
  feedback?: boolean;
}

/** The center of the footer: Suggestion Box and Bug Report, minus the page you're on. */
function FeedbackLinks({ linkClassName }: { linkClassName?: string }) {
  const { pathname, search } = useLocation();
  const links = [
    pathname !== '/suggest' && (
      <Link key="suggest" to="/suggest" className={linkClassName}>
        <span className={styles.settingsIcon} aria-hidden="true">
          <ModuleIcon name="lightbulb" size={14} strokeWidth={2} />
        </span>
        <span>Make a suggestion</span>
      </Link>
    ),
    pathname !== '/bugreport' && (
      <Link key="bug" to={bugReportHref(reportablePage(pathname, search))} className={linkClassName}>
        <span className={styles.settingsIcon} aria-hidden="true">
          <ModuleIcon name="bug" size={14} strokeWidth={2} />
        </span>
        <span>Report a bug</span>
      </Link>
    ),
  ].filter(Boolean);
  return (
    <div className={styles.feedback}>
      {links.map((link, i) => (
        <span key={i} className={styles.feedbackItem}>
          {i > 0 && (
            <span className={styles.midDot} aria-hidden="true">
              ·
            </span>
          )}
          {link}
        </span>
      ))}
    </div>
  );
}

export function SiteFooter({
  className,
  innerClassName,
  copyrightExtra,
  adminCopyrightHoneypot = false,
  settingsClassName,
  feedback = false,
}: SiteFooterProps) {
  const { openSettings } = useSiteSettings();
  const year = new Date().getFullYear();
  const linkClassName = cx(styles.settingsBtn, settingsClassName);
  return (
    <footer className={cx(styles.footer, className)}>
      <div className={cx(styles.footerInner, innerClassName)}>
        <span className={styles.credit}>
          {adminCopyrightHoneypot ? (
            <button
              type="button"
              className={styles.copyrightHoneypot}
              onClick={() => {
                window.location.assign('/admin');
              }}
            >
              ©
            </button>
          ) : (
            '©'
          )}{' '}
          {year} fmdwhsl
          {copyrightExtra}
        </span>
        {feedback && !isJoLunoHost() && <FeedbackLinks linkClassName={linkClassName} />}
        <button
          type="button"
          className={cx(linkClassName, styles.settings)}
          onClick={openSettings}
          aria-label="Open display settings"
          title="Display settings"
        >
          <span className={styles.settingsIcon} aria-hidden="true">
            <ModuleIcon name="settings" size={14} strokeWidth={2} />
          </span>
          <span>Settings</span>
        </button>
      </div>
    </footer>
  );
}
